/**
 * Model Attribute Calculation
 *
 * Calculates RPG-style attributes (Speed, Agility, Intelligence, Power) from
 * model benchmark data. Attack and Defense are the model's price, taken from the
 * LLMModelOption the backend returned -- never from a frontend table.
 */

import type { LLMModelAttributes, LLMAttributeColor, LLMModelOption } from '@/types';
import { MODEL_BENCHMARKS, BENCHMARK_REFS } from './modelBenchmarks';

export function calculateScore(value: number, min: number, max: number, inverted: boolean): number {
  if (max === min) return 50;
  let normalized = (value - min) / (max - min);
  normalized = Math.max(0, Math.min(1, normalized));
  if (inverted) normalized = 1 - normalized;
  return Math.max(1, Math.min(100, Math.round(normalized * 99 + 1)));
}

export function getAttributeColor(score: number): LLMAttributeColor {
  if (score < 20) return 'red';
  if (score < 40) return 'orange';
  if (score < 60) return 'yellow';
  if (score < 80) return 'green';
  return 'blue';
}

export const ATTRIBUTE_COLOR_CSS: Record<LLMAttributeColor, string> = {
  red: 'text-red-500',
  orange: 'text-orange-500',
  yellow: 'text-yellow-500',
  green: 'text-green-500',
  blue: 'text-blue-500',
};

export const ATTRIBUTE_BAR_CSS: Record<LLMAttributeColor, string> = {
  red: 'bg-red-500',
  orange: 'bg-orange-500',
  yellow: 'bg-yellow-500',
  green: 'bg-green-500',
  blue: 'bg-blue-500',
};

/**
 * Fixed color per attribute abbreviation.
 * Each RPG attribute has its own unique color for visual identity.
 */
export const ATTRIBUTE_FIXED_COLORS: Record<string, string> = {
  SPD: 'text-blue-400',
  AGI: 'text-green-400',
  INT: 'text-purple-400',
  PWR: 'text-orange-400',
  ATK: 'text-red-400',
  DEF: 'text-cyan-400',
};

export const ATTRIBUTE_FIXED_BAR_COLORS: Record<string, string> = {
  SPD: 'bg-blue-400',
  AGI: 'bg-green-400',
  INT: 'bg-purple-400',
  PWR: 'bg-orange-400',
  ATK: 'bg-red-400',
  DEF: 'bg-cyan-400',
};

export type CostColorLevel = 'free' | 'cheap' | 'moderate' | 'expensive' | 'premium';

export function getCostColor(cost: number): CostColorLevel {
  if (cost <= 1) return 'free';
  if (cost <= 5) return 'cheap';
  if (cost <= 20) return 'moderate';
  if (cost <= 100) return 'expensive';
  return 'premium';
}

export const COST_COLOR_CSS: Record<CostColorLevel, string> = {
  free: 'text-green-500',
  cheap: 'text-blue-500',
  moderate: 'text-yellow-500',
  expensive: 'text-orange-500',
  premium: 'text-red-500',
};

export function formatCostValue(cost: number): string {
  return String(cost);
}

/**
 * Format ATK/DEF raw cost for display.
 * Shows up to 2 decimal places, trimming trailing zeros.
 * An unknown price (null) shows as "—".
 * Examples: 15 -> "15", 0.01 -> "0.01", 3.50 -> "3.5", 0.20 -> "0.2"
 */
export function formatCostDisplay(value: number | null): string {
  if (value === null) return '\u2014'; // em dash for unknown/variable pricing
  return parseFloat(value.toFixed(2)).toString();
}

/**
 * Get normalized bar percentage (0-100) for ATK/DEF progress bars.
 * Uses log scale so both cheap ($0.01) and expensive ($120) models
 * have meaningful bar widths.
 */
/** Whether an abbreviation is a cost attribute (ATK/DEF use raw prices, not 1-100 scores) */
export function isCostAttribute(abbrev: string): boolean {
  return abbrev === 'ATK' || abbrev === 'DEF';
}

export function getCostBarPercent(value: number | null, type: 'attack' | 'defense'): number {
  const max = type === 'attack' ? BENCHMARK_REFS.price_input_max : BENCHMARK_REFS.price_output_max;
  if (value === null || value <= 0) return 0; // no bar: unknown or free
  if (value >= max) return 100;
  // Log scale: maps range [0.01, max] -> [1, 100] with better distribution
  const logMin = Math.log(0.01);
  const logMax = Math.log(max);
  const logVal = Math.log(Math.max(0.01, value));
  const normalized = (logVal - logMin) / (logMax - logMin);
  return Math.max(1, Math.min(100, Math.round(normalized * 99 + 1)));
}

/**
 * Bar width (0-100) and label for one attribute row. SPD/AGI/INT/PWR hold a
 * 1-100 score; ATK/DEF hold a price in $/1M tokens, or null when it is unknown.
 */
export function getAttributeDisplay(
  abbrev: string,
  value: number | null
): { barPercent: number; label: string } {
  if (isCostAttribute(abbrev)) {
    return {
      barPercent: getCostBarPercent(value, abbrev === 'ATK' ? 'attack' : 'defense'),
      label: formatCostDisplay(value),
    };
  }
  if (value === null) throw new Error(`${abbrev} is a 1-100 score and is never null`);
  return { barPercent: value, label: String(value) };
}

/**
 * A backend price as ATK/DEF shows it: null when the row carries none (a
 * placeholder or the offline fallback list) or when it is negative -- the
 * OpenRouter router aliases (openrouter/auto) publish -1 per token because the
 * price is decided per request.
 */
function knownPrice(price: number | undefined): number | null {
  return price === undefined || price < 0 ? null : price;
}

/**
 * Input + output price per 1M tokens as the backend prices the model, or null
 * when either is unknown. Needs no benchmark row: the price is the backend's,
 * so a model the stats table does not know yet still sorts by price.
 */
export function getModelTotalPrice(model: LLMModelOption): number | null {
  const input = knownPrice(model.price_input);
  const output = knownPrice(model.price_output);
  return input === null || output === null ? null : input + output;
}

export function getModelAttributes(model: LLMModelOption): LLMModelAttributes | null {
  const benchmark = MODEL_BENCHMARKS[model.value];
  if (!benchmark) return null;

  return {
    speed: calculateScore(
      benchmark.latency_ms,
      BENCHMARK_REFS.latency_min,
      BENCHMARK_REFS.latency_max,
      true
    ),
    agility: calculateScore(
      benchmark.throughput_tps,
      BENCHMARK_REFS.throughput_min,
      BENCHMARK_REFS.throughput_max,
      false
    ),
    intelligence: calculateScore(
      benchmark.context_length,
      BENCHMARK_REFS.context_min,
      BENCHMARK_REFS.context_max,
      false
    ),
    power: calculateScore(
      benchmark.max_output,
      BENCHMARK_REFS.output_min,
      BENCHMARK_REFS.output_max,
      false
    ),
    // ATK/DEF: raw cost per million tokens ($/M), as the backend prices the model
    attack: knownPrice(model.price_input),
    defense: knownPrice(model.price_output),
  };
}
